import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import isEqual from 'lodash/isEqual';
import Multiselect from 'multiselect-react-dropdown';
import { useEffect, useRef, useState } from 'react';
import { formatDataFromLocalStorage, validateEmail } from '../utilities/helper';
import { onboardingApplication } from '../service/apiServices';
import { useAzureData } from '../context/AzureContext';
import './OnboardingForm.css';
import SearchDropDown from './SearchDropDown';
type Env = {
  label: string;
  value: string;
};

type AppOption = {
  label: string;
  value: string;
};
type SPNAppIdEnvironmentMap = {
  environment: string;
  service_principal_name: string;
  application_id: string;
};
type ModelDetails = {
  model_id: string;
  model_name: string;
  prod: string;
  'non-prod': string;
};
type OnboardingFormValue = {
  onboarding_id?: number;
  environment: string[];
  application_name: string;
  model_name: string[];
  model_id: string[];
  primary_owner: string;
  wl3_approver: string;
  application_description: string;
  secondary_owner: string;
  icc: string;
  cc: string;
  
  aif_rag_access: boolean;
  ai_inventory_id: string;
  onboardingstatus: string | null;
  spn_appid_env_map: SPNAppIdEnvironmentMap[] | [];
  adgroupforappaccess: string[] | [];
  tier: string;
};

const OnboardingForm = (props: any) => {
  const availableEnvs = [
    { label: 'Non-Prod', value: 'non-prod' },
    { label: 'Prod', value: 'prod' }
  ];
  const {
    listofAppIdentities,
    formData,
    listOfModels,
    pricingDetails,
    unEditableFields,
    handleNavigation
  } = props;
  const envRef = useRef<any>(null);
  const modelRef = useRef<any>(null);
  const [formValue, setFormValue] = useState<OnboardingFormValue>(formData);
  const [initialFormData, setInitialFormData] = useState(formValue);
  const [disableSubmitButton, setDisableSubmitButton] = useState(true);
  const [selectedEnvs, setSelectedEnvs] = useState<Env[] | []>([]);
  const [isEmailValid, setisEmailValid] = useState({
    primary_owner: true,
    wl3_approver: true,
    secondary_owner: true
  });
  const [spnValidation, setSpnValidation] = useState('');
  const [dropDownOptions, setDropDownOptions] = useState<AppOption[] | []>([]);

  const { apiToken } = useAzureData();

  useEffect(() => {
    setFormValue((prevState) => {
      return {
        ...prevState,
        adgroupforappaccess: [...new Set(addAccessgroupInfo(prevState))]
      };
    });
  }, [formValue.model_id, formValue.environment]);
  useEffect(() => {
    const validEmails = Object.values(isEmailValid).every(Boolean);
    const isEnvSelected = formValue.environment.length > 0;
    const isModelSelected = formValue.model_name.length > 0;
    const isFormModified =
      isEnvSelected && isModelSelected && validEmails && !isEqual(formValue, initialFormData);
    setDisableSubmitButton(!isFormModified);
  }, [formValue, initialFormData, isEmailValid]);

  useEffect(() => {
    if (formValue.environment.length > 0) {
      const preselectedValues = formValue.environment.flatMap((env) =>
        availableEnvs.filter((avEnv) => avEnv.value.toLowerCase() === env.toLowerCase())
      );
      setSelectedEnvs(preselectedValues);
    }

    //formatting the data before sending the option to <SearchDropdown> compo
    const formattedData = formatDataFromLocalStorage(listofAppIdentities);
    setDropDownOptions(formattedData);
  }, []);

  const addAccessgroupInfo = (prevState: OnboardingFormValue) => {
    const selectedModels = listOfModels.filter((md: any) =>
      prevState.model_name?.includes(md.model_name)
    );
    const updatedAccessGroupDetails = prevState.environment.flatMap((env) =>
      selectedModels.map((model: any) => model[env.toLowerCase()])
    );
    return updatedAccessGroupDetails;
  };

  const shouldSave = (spnValue: SPNAppIdEnvironmentMap[]) => {
    let shouldSave = true;
    if (spnValue.length === 0) {
      setSpnValidation('Please add SPN name');
      shouldSave = false;
    } else if (spnValue.length === 2) {
      if (spnValue[0].service_principal_name === spnValue[1].service_principal_name) {
        setSpnValidation('SPN value for different environments cannot be same');
        shouldSave = false;
      }
    }
    const emptySpnValue = spnValue.filter((value) => value.service_principal_name.trim() === '');
    if (emptySpnValue.length > 0) {
      setSpnValidation('Please add SPN name');
      shouldSave = false;
    }
    return shouldSave;
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!shouldSave(formValue.spn_appid_env_map)) return;
    setDisableSubmitButton(true);
    console.log(formValue);
    //post the values
    if (unEditableFields) {
      setFormValue((prevState) => ({ ...prevState, onboarding_id: formValue.onboarding_id }));
    }

    const response = await onboardingApplication(formValue, apiToken);
    console.log(response);

    resetForm();
    handleNavigation();
  };
  const resetForm = () => {
    setFormValue({
      environment: [],
      application_name: '',
      model_name: [],
      model_id: [],
      primary_owner: '',
      wl3_approver: '',
      application_description: '',
      secondary_owner: '',
      icc: '',
      cc: '',
      aif_rag_access: false,
      ai_inventory_id: '',
      onboardingstatus: null,
      spn_appid_env_map: [],
      adgroupforappaccess: [],
      tier: ''
    });
    setSelectedEnvs([]);
    if (envRef.current) envRef.current.resetSelectedValues();
    if (modelRef.current) modelRef.current.resetSelectedValues();
    setDisableSubmitButton(true);
  };

  //handling app_id and spn info for each selected environments
  const handleEnvInfo = (value: string, env: string) => {
    setSpnValidation('');
    setFormValue((prevState) => {
      //check if the array has the environment in question present
      const existingEnv = prevState.spn_appid_env_map.find((data) => data.environment === env);
      let selectedAppIdentity: any;

      //if present then update the existing the correct one by looping through
      //if not present then add new entry into array
      if (value) {
        selectedAppIdentity = listofAppIdentities.find(
          (appIdentity: any) => appIdentity.spn_name === value
        ).application_id;
      }

      const updatedCredentials = existingEnv
        ? prevState.spn_appid_env_map.map((data) =>
            data.environment === env
              ? {
                  ...data,
                  service_principal_name: value || '',
                  application_id: selectedAppIdentity || ''
                }
              : data
          )
        : [
            ...(prevState.spn_appid_env_map || []),
            {
              environment: env,
              service_principal_name: value || '',
              application_id: selectedAppIdentity || ''
            }
          ];

      //return the previous form values
      //and update creds
      return {
        ...prevState,
        spn_appid_env_map: updatedCredentials
      };
    });
  };

  //Handling Environment selection from dropdown
  const handleEnvSelection = async (envs: Env[]) => {
    setSelectedEnvs([...envs]);
    const environments = envs.map((env) => env.value);
    setFormValue((prev) => ({ ...prev, environment: [...environments] }));
  };

  //Handling Environment removal from dropdown
  const handleEnvRemoval = async (envs: Env[]) => {
    setSelectedEnvs([...envs]);

    const environments = envs.map((env) => env.value);
    setFormValue((prev) => ({ ...prev, environment: [...environments] }));

    setFormValue((prevState) => {
      const updatedCredentials = prevState.spn_appid_env_map.filter((data) =>
        environments.includes(data.environment)
      );
      return {
        ...prevState,
        spn_appid_env_map: updatedCredentials
      };
    });
  };

  //Handling of Model Selection
  const handleModelsSelection = async (models: ModelDetails[]) => {
    const modelNames = models.map((model) => model.model_name);
    const modelIds = models.map((model) => model.model_id);
    setFormValue((prevState) => ({
      ...prevState,
      model_name: [...modelNames],
      model_id: [...modelIds]
    }));
  };

  //Handling of Model Removal
  const handleModelsRemoval = async (models: ModelDetails[]) => {
    const modelNames = models.map((model) => model.model_name);
    const modelIds = models.map((model) => model.model_id);
    setFormValue((prevState) => ({
      ...prevState,
      model_name: [...modelNames],
      model_id: [...modelIds]
    }));
  };

  // handle changes in primary_owner, secondary_owner and wl3+ approver
  const handleValueChange = (e: any, label: string) => {
    setFormValue((prevValue) => ({
      ...prevValue,
      [label]: e.target.value.trim()
    }));
    setisEmailValid((prevState) => ({ ...prevState, [label]: true }));
    if (e.target.value !== '') {
      if (!validateEmail(e.target.value)) {
        setisEmailValid((prevState) => ({ ...prevState, [label]: false }));
      }
    }
  };


  const renderEnvMultiSelector = () => {
    // const selectedValues = availableEnvs.filter((env) =>
    //   formValue?.environment.includes(env.value.toLowerCase())
    const selectedValues = formValue.environment.flatMap((t) =>
      availableEnvs.filter((av) => av.value.toLowerCase() === t.toLowerCase())
    );

    return (
      <Multiselect
        options={availableEnvs}
        selectedValues={selectedValues}
        showCheckbox={true}
        showArrow={true}
        onSelect={handleEnvSelection}
        onRemove={handleEnvRemoval}
        displayValue="label"
        hidePlaceholder={true}
        ref={envRef}
        id="env"
      />
    );
  };
  const renderModelSelector = () => {
    const selectedValues = listOfModels.filter((model: any) =>
      formValue?.model_name?.includes(model.model_name)
    );
    return (
      <Multiselect
        options={listOfModels}
        selectedValues={selectedValues}
        showCheckbox={true}
        showArrow={true}
        displayValue="model_name"
        ref={modelRef}
        id="model"
        onSelect={handleModelsSelection}
        onRemove={handleModelsRemoval}
      />
    );
  };

  return (
    <div className="row pt-4 pb-2 mb-3  text-left ">
      <Tabs defaultActiveKey="home" id="uncontrolled-tab-example" className="mb-3 rounded-3">
        <Tab eventKey="home" title="App Onboarding">
          <form className="form-container" onSubmit={handleSubmit}>
            <div className="d-flex">
              <div className="col p-3">
                <div className="row">
                  <div className="col-md mb-2">
                    <div className="mb-1">
                      <p className="mb-1 unl-label-p">Environment*</p>
                      {renderEnvMultiSelector()}
                      <p style={{ color: 'red' }}>{spnValidation}</p>
                    </div>

                    {selectedEnvs.length > 0 &&
                      selectedEnvs.map((env, index) => (
                        <div key={index}>
                          <p className="my-1 unl-label-p">
                            Service Principal Name for {env.label} (SPN)*
                          </p>
                          <SearchDropDown
                            options={dropDownOptions}
                            selectedVal={
                              formValue?.spn_appid_env_map[index]?.service_principal_name || ''
                            }
                            placeholder="Search SPN..."
                            handleChange={(val: string) => {
                              handleEnvInfo(val, env.label);
                            }}
                          />
                        </div>
                      ))}

                    <p className="my-1 unl-label-p">Application Name*</p>
                    <input
                      type="text"
                      className={`${
                        unEditableFields ? 'form-control non-editable' : `form-control`
                      }`}
                      onChange={(e) =>
                        setFormValue((prevValue) => ({
                          ...prevValue,
                          application_name: e.target.value
                        }))
                      }
                      value={formValue.application_name}
                      name="app_name"
                      required
                      readOnly={unEditableFields}
                    />

                    <p className="my-1 unl-label-p">Primary Owner*</p>
                    <input
                      type="text"
                      onChange={(e) => handleValueChange(e, 'primary_owner')}
                      className={`${
                        unEditableFields ? 'form-control non-editable' : `form-control`
                      }`}
                      name="primary_owner"
                      value={formValue.primary_owner}
                      required
                      readOnly={unEditableFields}
                    />
                    {!isEmailValid.primary_owner && (
                      <p style={{ color: 'red' }}>Invalid email address</p>
                    )}

                    <p className="my-1 unl-label-p">WL3+ Approver*</p>
                    <input
                      type="text"
                      onChange={(e) => handleValueChange(e, 'wl3_approver')}
                      className="form-control"
                      name="wl3_approver"
                      value={formValue.wl3_approver}
                      required
                    />
                    {!isEmailValid.wl3_approver && (
                      <p style={{ color: 'red' }}>Invalid email address</p>
                    )}

                    <p className="my-1 unl-label-p">AI Inventory ID</p>
                    <input
                      type="text"
                      onChange={(e) =>
                        setFormValue((prevValue) => ({
                          ...prevValue,
                          ai_inventory_id: e.target.value.trim()
                        }))
                      }
                      className="form-control"
                      name="ai_inventory_id"
                      value={formValue.ai_inventory_id}
                    />

                    <p className="my-1 unl-label-p">Model Name*</p>
                    {listOfModels && renderModelSelector()}
                  </div>

                  <div className="col-md ">
                    <p className="my-1 unl-label-p">Tier</p>

                    <select
                      value={formValue?.tier.toLowerCase() || ''}
                      className="form-select"
                      onChange={(e) => {
                        setFormValue((prevState) => ({
                          ...prevState,
                          tier: e.target.value
                        }));
                      }}
                      id="tierselection">
                      <option value=""></option>
                      {pricingDetails.map((item: any, index: number) => (
                        <option key={index} value={item.tier.toLowerCase()}>
                          {item.tier}
                        </option>
                      ))}
                    </select>

                    <p className="my-1 unl-label-p">Application Description*</p>
                    <textarea
                      className="form-control"
                      name="desc"
                      onChange={(e) =>
                        setFormValue((prevValue) => ({
                          ...prevValue,
                          application_description: e.target.value
                        }))
                      }
                      value={formValue.application_description}
                      required
                      readOnly={unEditableFields}
                    />
                    <p className="my-1 unl-label-p">Secondary Owner</p>
                    <input
                      type="text"
                      className=" form-control"
                      name="secondary_owner"
                      onChange={(e) => handleValueChange(e, 'secondary_owner')}
                      value={formValue.secondary_owner}
                    />
                    {!isEmailValid.secondary_owner && (
                      <p style={{ color: 'red' }}>Invalid email address</p>
                    )}

                    <p className="my-1 unl-label-p">ICC*</p>
                    <input
                      type="text"
                      className=" form-control"
                      name="icc"
                      onChange={(e) =>
                        setFormValue((prevValue) => ({
                          ...prevValue,
                          icc: e.target.value.trim()
                        }))
                      }
                      value={formValue.icc}
                      required
                    />
                    <p className="my-1 unl-label-p">CC*</p>
                    <input
                      type="text"
                      className=" form-control"
                      name="cc"
                      onChange={(e) =>
                        setFormValue((prevValue) => ({
                          ...prevValue,
                          cc: e.target.value.trim()
                        }))
                      }
                      value={formValue.cc}
                      required
                    />

                    <div className="mt-2">
                      <input
                        id="RAG"
                        type="checkbox"
                        onChange={() =>
                          setFormValue((prevValue) => ({
                            ...prevValue,
                            aif_rag_builder_access: !prevValue.aif_rag_access
                          }))
                        }
                        checked={formValue.aif_rag_access}
                        readOnly={unEditableFields}
                      />

                      <label className="ps-2 mb-1 unl-label-p" htmlFor="RAG">
                        Subscribe to AI Foundation RAG Builder
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-3 pt-3 button-container">
              <button
                type="submit"
                className="btn  uniBtnPurple"
                style={{ color: 'white' }}
                disabled={disableSubmitButton}>
                {unEditableFields ? 'Update' : 'Onboard'}
              </button>
              <button
                type="reset"
                style={{ color: 'white' }}
                className="btn uniBtnPurple"
                onClick={resetForm}>
                Clear
              </button>
            </div>
          </form>
        </Tab>
      </Tabs>
    </div>
  );
};

export default OnboardingForm;
